import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { outfitsApi } from '../../../../api/outfits.api';
import { isAlreadyGone } from '../../../../api/client';
import { toast } from '../../../ui/Toast';
import { useAiDataVersion } from '../../../ai/AiPanelContext';
import type { ChecklistItem, ChecklistSection, ItineraryDay, OutfitGroup, OutfitPiece } from '../../../../types';
import OutfitDayCard, { type DayPiece } from './OutfitDayCard';
import { Shirt, Icon } from '../../../ui/Icon';

interface Props {
  tripId: string;
  days: ItineraryDay[];
  /** Whose clothes: you, or the member whose tab is open (read-only). */
  ownerId: string;
  readOnly: boolean;
  /** Every suitcase item the viewer can see, to join pieces with. */
  itemsById: Map<string, ChecklistItem>;
  /** The owner's suitcase, by section: what a piece can be made of (ADR-0018). */
  sections: ChecklistSection[];
  /** The clothing section, picked when the input opens. */
  defaultSectionId: string | null;
  /** Puts a garment in the given section (or the clothing one) and returns it. */
  onCreateGarment: (text: string, sectionId: string | null) => Promise<ChecklistItem | null>;
}

/** Today while the trip is on, the first day before it, the last one after. */
function initialOpenDay(days: ItineraryDay[]): string | null {
  if (days.length === 0) return null;
  const today = format(new Date(), 'yyyy-MM-dd');
  const current = days.find(d => d.date.slice(0, 10) === today);
  if (current) return current.id;
  return today < days[0].date.slice(0, 10) ? days[0].id : days[days.length - 1].id;
}

/**
 * What each traveller plans to wear, day by day, built from their suitcase.
 * ADR-0017: a piece references a suitcase item, so its text and whether it is
 * packed are read from the suitcase — and a piece whose item was deleted from
 * the suitcase is gone on the server too (Cascade), so it is simply not shown.
 */
export default function OutfitPlanner({
  tripId, days, ownerId, readOnly, itemsById, sections, defaultSectionId, onCreateGarment,
}: Props) {
  const { t } = useTranslation();
  const [pieces, setPieces] = useState<OutfitPiece[]>([]);
  const [groups, setGroups] = useState<OutfitGroup[]>([]);
  const [openDayId, setOpenDayId] = useState(() => initialOpenDay(days));

  // ADR-0028: re-read when the assistant planned or removed a piece.
  const aiPackingVersion = useAiDataVersion('packing');
  useEffect(() => {
    let cancelled = false;
    outfitsApi.list(tripId)
      .then(plan => { if (!cancelled) { setPieces(plan.pieces); setGroups(plan.groups); } })
      .catch(() => { if (!cancelled) toast(t('packing.outfits.loadFailed'), 'error'); });
    return () => { cancelled = true; };
  }, [tripId, t, aiPackingVersion]);

  const shown = useMemo(() => {
    const byDay = new Map<string, DayPiece[]>();
    for (const p of pieces) {
      if (p.ownerUserId !== ownerId) continue;
      const item = itemsById.get(p.checklistItemId);
      if (!item) continue;
      const list = byDay.get(p.dayId) ?? [];
      list.push({ id: p.id, checklistItemId: p.checklistItemId, groupId: p.groupId, text: item.text, checked: item.checked });
      byDay.set(p.dayId, list);
    }
    return byDay;
  }, [pieces, ownerId, itemsById]);

  async function pick(dayId: string, groupId: string | null, itemId: string) {
    try {
      const piece = await outfitsApi.addPiece(tripId, { dayId, checklistItemId: itemId, groupId });
      setPieces(ps => [...ps, piece]);
    } catch {
      toast(t('packing.outfits.addFailed'), 'error');
    }
  }

  async function create(dayId: string, groupId: string | null, text: string, sectionId: string | null) {
    const item = await onCreateGarment(text, sectionId);
    if (item) await pick(dayId, groupId, item.id);
  }

  /** Optimistic: the chip lands where it was dropped, and goes back if the server says no. */
  async function move(pieceId: string, groupId: string | null) {
    const before = pieces.find(p => p.id === pieceId);
    if (!before || before.groupId === groupId) return;
    setPieces(ps => ps.map(p => p.id === pieceId ? { ...p, groupId } : p));
    try {
      await outfitsApi.movePiece(tripId, pieceId, groupId);
    } catch {
      setPieces(ps => ps.map(p => p.id === pieceId ? { ...p, groupId: before.groupId } : p));
      toast(t('common.error'), 'error');
    }
  }

  async function remove(pieceId: string) {
    try {
      await outfitsApi.removePiece(tripId, pieceId);
    } catch (e) {
      if (!isAlreadyGone(e)) { toast(t('common.error'), 'error'); return; }
    }
    setPieces(ps => ps.filter(p => p.id !== pieceId));
  }

  async function createGroup(dayId: string, title: string) {
    try {
      const group = await outfitsApi.createGroup(tripId, { dayId, title });
      setGroups(gs => [...gs, group]);
    } catch {
      toast(t('common.error'), 'error');
    }
  }

  async function renameGroup(groupId: string, title: string) {
    try {
      const updated = await outfitsApi.renameGroup(tripId, groupId, title);
      setGroups(gs => gs.map(g => g.id === groupId ? updated : g));
    } catch {
      toast(t('common.error'), 'error');
    }
  }

  async function deleteGroup(groupId: string) {
    try {
      await outfitsApi.deleteGroup(tripId, groupId);
    } catch (e) {
      if (!isAlreadyGone(e)) { toast(t('common.error'), 'error'); return; }
    }
    setGroups(gs => gs.filter(g => g.id !== groupId));
    // Mirrors the server's SetNull: the pieces stay on the day, ungrouped.
    setPieces(ps => ps.map(p => p.groupId === groupId ? { ...p, groupId: null } : p));
  }

  if (days.length === 0) return null;

  return (
    <section className="mt-8">
      <div className="flex items-center gap-2 mb-1">
        <Icon as={Shirt} size={16} className="text-violet-600" />
        <h3 className="font-serif italic font-semibold text-lg text-slate-900 dark:text-slate-100">
          {t('packing.outfits.heading')}
        </h3>
      </div>
      <p className="text-xs text-slate-400 dark:text-slate-500 mb-4">{t('packing.outfits.hint')}</p>

      <div className="space-y-3">
        {days.map(day => (
          <OutfitDayCard
            key={day.id}
            day={day}
            open={openDayId === day.id}
            onToggle={() => setOpenDayId(id => id === day.id ? null : day.id)}
            pieces={shown.get(day.id) ?? []}
            groups={groups.filter(g => g.dayId === day.id && g.ownerUserId === ownerId)}
            sections={sections}
            defaultSectionId={defaultSectionId}
            readOnly={readOnly}
            onPick={(groupId, itemId) => pick(day.id, groupId, itemId)}
            onCreate={(groupId, text, sectionId) => create(day.id, groupId, text, sectionId)}
            onRemove={remove}
            onMove={move}
            onCreateGroup={title => createGroup(day.id, title)}
            onRenameGroup={renameGroup}
            onDeleteGroup={deleteGroup}
          />
        ))}
      </div>
    </section>
  );
}
